import cn from 'classnames'
import { useRouter } from 'next/navigation'
import { FC, Fragment, useCallback, useMemo, useState } from 'react'

import { Button } from '@/components/Button'
import { NavigationLink } from '@/store/NavigationMenu/NavigationTypes'

import { NavigationArticle } from '../NavigationArticle'
import { NavigationBack } from '../NavigationBack'
import { NavigationContacts } from '../NavigationContacts'
import { NavigationMenuSecondLevelItem } from '../NavigationMenuSecondLevelItem'
import { NavigationMenuThirdLevel } from '../NavigationMenuThirdLevel'

import { NavigationLinkThirdLevelMobile } from '../NavigationLinkThirdLevelMobile'
import styles from './NavigationDropdown.module.scss'

type SubNavigationItemWithSubmenu = Omit<NavigationLink, 'submenu'> & {
  submenu: NavigationLink[]
}

interface NavigationDropdownProps {
  isVisible: boolean
  title: string
  menuList: NavigationLink[]
  onHide: () => void
  onLinkClick?: () => void
  telegram: string
  whatsapp: string
  max: string
}
export const NavigationDropdown: FC<NavigationDropdownProps> = ({
  isVisible,
  title,
  menuList,
  onHide,
  onLinkClick,
  telegram,
  whatsapp,
  max,
}) => {
  const router = useRouter()
  const [titleVisibleThirdLevelMenu, setTitleVisibleThirdLevelMenu] = useState<
    string | null
  >(menuList[0]?.title ?? null)

  const [visibleThirdLevelMenu, setVisibleThirdLevelMenu] = useState(false)

  const showThirdLevelMenu = useCallback(
    (title: string) => {
      const menuItem = menuList.find((item) => item.title === title)

      if (menuItem != null && menuItem.children.length !== 0) {
        if (window.innerWidth < 1485) {
          setTitleVisibleThirdLevelMenu((t) => (t === title ? null : title))
        } else {
          setTitleVisibleThirdLevelMenu(title)
        }
        setVisibleThirdLevelMenu(true)
      }
    },
    [menuList]
  )

  const hideThirdLevelMenu = useCallback(() => {
    setTitleVisibleThirdLevelMenu(null)
    setVisibleThirdLevelMenu(false)
  }, [])

  const menuLinkClick = useCallback(() => {
    if (onLinkClick != null) {
      onLinkClick()
    }
    onHide()
  }, [onHide, onLinkClick])

  const showAll = useCallback(() => {
    menuLinkClick()
    router.push('/uslugi')
  }, [menuLinkClick, router])

  const listWithThirdLevel: SubNavigationItemWithSubmenu[] = useMemo(() => {
    return menuList.filter(
      (item): item is SubNavigationItemWithSubmenu => item.children.length !== 0
    )
  }, [menuList])

  return (
    <div
      className={cn(styles.container, {
        [styles.isHidden]: !isVisible,
      })}
    >
      <div className={styles.backButton}>
        <NavigationBack title={title} onClick={onHide} />
      </div>

      {/* SECOND LEVEL MENU */}
      <div className={styles.menuSecondContainer}>
        <div
          className={cn(styles.menuSecond, {
            [styles.thirdIsVisible]: visibleThirdLevelMenu,
          })}
        >
          {menuList.map((item) => (
            <Fragment key={item.title}>
              <NavigationMenuSecondLevelItem
                isActive={item.title === titleVisibleThirdLevelMenu}
                title={item.title}
                href={item.href}
                icon={item.icon}
                withSubMenu={item.children.length !== 0}
                onShowSubMenu={showThirdLevelMenu}
                onClickLink={menuLinkClick}
              />
              {item.children.length !== 0 ? (
                <div
                  className={cn(styles.mobileMenuThirdContainer, {
                    [styles.isVisible]:
                      item.title === titleVisibleThirdLevelMenu,
                  })}
                >
                  <div className={styles.mobileMenuThirdList}>
                    {item.children.map((item) => (
                      <NavigationLinkThirdLevelMobile
                        key={item.title}
                        item={item}
                        onLinkClick={onLinkClick}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </Fragment>
          ))}
        </div>

        <Button variant="opacity" className={styles.showAll} onClick={showAll}>
          Cмотреть все
        </Button>
        <NavigationArticle
          className={cn(styles.articleContainer, styles.isShowOnMobile)}
          onOpen={menuLinkClick}
        />
        <NavigationContacts
          className={styles.navigationContacts}
          telegram={telegram}
          whatsapp={whatsapp}
          max={max}
        />
      </div>

      {/* THIRD LEVEL MENU */}
      <div
        className={cn(styles.menuThirdContainer, {
          [styles.isVisible]: visibleThirdLevelMenu,
        })}
      >
        <div className={styles.menuThird}>
          {listWithThirdLevel.map((menuItem) => (
            <NavigationMenuThirdLevel
              isVisible={menuItem.title === titleVisibleThirdLevelMenu}
              key={menuItem.title}
              title={menuItem.title}
              subMenu={menuItem.children}
              onBack={hideThirdLevelMenu}
              onLinkClick={menuLinkClick}
              onShowAll={showAll}
            />
          ))}
        </div>

        <NavigationArticle
          className={styles.articleContainer}
          onOpen={menuLinkClick}
        />
        <NavigationContacts
          className={styles.navigationContacts}
          telegram={telegram}
          whatsapp={whatsapp}
          max={max}
        />
      </div>
    </div>
  )
}
